// components
import { Box } from '@mui/material';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import SpecialLeaveTable from './SpecialLeaveTable';
import SpecialLeaveFields from './SpecialLeaveFields';

// constants
import { NOMINAL_DUTY_FIELDS } from './SpecialLeaveTab.consts';

// external libraries
import { AnimatePresence } from 'motion/react';

// hooks
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useGetDutyHourTypesTableData } from 'components/admin-portal/Tables/Settings/DutyHourTypes/hooks/useGetDutyHourTypesTableData';
import { useGetSpecialLeaveColumns } from 'components/admin-portal/CLA/CLASettings/hooks/useGetSpecialLeaveColumns';

// styles
import { StyledContainerBox } from './SpecialLeaveTab.styles';

// types
import { DccFlag } from 'components/types';
import { TabProps } from 'components/admin-portal/CLA/CLASettings/CLASettings.types';
import { FormValues } from './SpecialLeaveTab.types';
import { GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';

// utils
import { v4 as uuidv4 } from 'uuid';

// validation schema
import { addRowSchema, buildSaveSchema } from './SpecialLeaveTab.schema';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { buildExistingCodes, stringNorm } from './SpecialLeaveTab.utils';
import { EditNominalDutyParams } from './SpecialLeaveTable.types';

const SpecialLeaveTab = ({ setCurrentSchema, isEditable }: TabProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla.specialLeave.settings'
  });
  const {
    watch,
    control,
    setValue,
    resetField,
    setError,
    clearErrors,
    setFocus
  } = useFormContext<FormValues>();
  const { data: dhtOptions } = useGetDutyHourTypesTableData();

  const [editingRowId, setEditingRowId] = useState<number | null>(null);

  const {
    fields: specialLeavesRows,
    append,
    update,
    remove
  } = useFieldArray({
    control,
    name: 'special_leaves',
    keyName: 'specialLeavesTabKey'
  });

  const [
    specialLeaveImportCode,
    specialLeaveDutyHourTypeId,
    specialLeaveFteLeaveHoursImportCode,
    specialLeaveFtePlbHoursImportCode,
    specialLeaveFteHolidayCompensationImportCode,
    plbName,
    specialLeavePercentage,
    specialLeaveProcessAsNegative,
    specialLeavePeriod
  ] = useWatch({
    control,
    name: [
      'specialLeaveImportCode',
      'specialLeaveDutyHourTypeId',
      'specialLeaveFteLeaveHoursImportCode',
      'specialLeaveFtePlbHoursImportCode',
      'specialLeaveFteHolidayCompensationImportCode',
      'plbName',
      'specialLeavePercentage',
      'specialLeaveProcessAsNegative',
      'specialLeavePeriod'
    ]
  });

  const filteredDhtOptions =
    dhtOptions &&
    [...dhtOptions]
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''))
      .filter(({ dcc }) => dcc?.flags?.includes(DccFlag.ALLOWED_WITH_NOMINAL))
      .map((item) => ({
        label: item.description ?? '',
        value: item.dutyTypeId
      }));

  const columns = useGetSpecialLeaveColumns({
    specialLeavesRows,
    dhtOptions
  });

  const usedDutyHourTypeIds = specialLeavesRows
    .map((row) => {
      const disabledOption = filteredDhtOptions?.find(
        (option) => option.value === row.special_leave_dutyHourTypeId
      );
      return disabledOption?.value;
    })
    .filter(Boolean);

  const dutyHourTypeOptions = filteredDhtOptions?.map((option) => ({
    ...option,
    disabled: usedDutyHourTypeIds.includes(option.value)
  }));

  const existingImportCodes = [
    ...specialLeavesRows
      .map((row) => stringNorm(row.special_leave_importCode))
      .filter(Boolean),
    stringNorm(specialLeaveFteLeaveHoursImportCode),
    stringNorm(specialLeaveFtePlbHoursImportCode),
    stringNorm(specialLeaveFteHolidayCompensationImportCode)
  ].filter(Boolean);

  const resetSpecialLeaveFields = () =>
    NOMINAL_DUTY_FIELDS.forEach((field) =>
      resetField(field.value, { defaultValue: '' })
    );

  const setValueSpecialLeaveFields = (row: GridValidRowModel | undefined) =>
    NOMINAL_DUTY_FIELDS.forEach((field) =>
      setValue(field.value, row?.[field.key])
    );

  // NOTE: before submitting the form we use handleAddRow and handleDeleteRows for adding the special leave rows in the table.
  // This is only added in the frontend, form submission / calling api is handled in the parent component (EditCLA)
  const handleAddOrUpdateRow = async () => {
    const existingImportCodes = buildExistingCodes(
      specialLeavesRows,
      editingRowId,
      watch('specialLeaveFteLeaveHoursImportCode'),
      watch('specialLeaveFtePlbHoursImportCode'),
      watch('specialLeaveFteHolidayCompensationImportCode')
    );

    try {
      await addRowSchema.validate(
        {
          specialLeaveImportCode,
          specialLeaveDutyHourTypeId,
          specialLeavePercentage
        },
        { abortEarly: false, context: { existingImportCodes } }
      );
      clearErrors([
        'specialLeaveImportCode',
        'specialLeaveDutyHourTypeId',
        'specialLeavePercentage'
      ]);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) =>
          setError(err.path as keyof FormValues, {
            type: 'manual',
            message: err.message
          })
        );
      }
      return;
    }

    if (
      !specialLeaveImportCode ||
      !specialLeaveDutyHourTypeId ||
      (editingRowId === null &&
        usedDutyHourTypeIds.includes(Number(specialLeaveDutyHourTypeId)))
    )
      return;

    const payload = {
      special_leave_id:
        editingRowId !== null
          ? specialLeavesRows[editingRowId].special_leave_id
          : `tmp-${uuidv4()}`,
      special_leave_importCode: specialLeaveImportCode,
      special_leave_dutyHourTypeId: Number(specialLeaveDutyHourTypeId),
      special_leave_percentage:
        specialLeavePercentage === '' || specialLeavePercentage == null
          ? undefined
          : Number(specialLeavePercentage),
      special_leave_processAsNegative: specialLeaveProcessAsNegative,
      special_leave_period: specialLeavePeriod
    };

    if (editingRowId !== null) {
      update(editingRowId, payload);
      setEditingRowId(null);
    } else {
      append(payload);
    }

    resetSpecialLeaveFields();
  };

  const handleDeleteRows = (ids: GridRowId[]) => {
    const indices = specialLeavesRows
      .map((row, idx) => ({ id: row.special_leave_id, idx }))
      .filter(({ id }) => ids.includes(id))
      .map(({ idx }) => idx)
      .sort((a, b) => b - a);

    remove(indices);
  };

  const handleEditNominalDuty = ({ id, row }: EditNominalDutyParams) => {
    const rowId = specialLeavesRows.findIndex(
      (row) => row.special_leave_id === id
    );
    if (rowId === -1) return;

    setEditingRowId(rowId);
    setValueSpecialLeaveFields(row);
    setFocus('specialLeaveImportCode');
  };

  useEffect(() => {
    setCurrentSchema(buildSaveSchema(existingImportCodes));
  }, [
    specialLeavesRows,
    specialLeaveFteLeaveHoursImportCode,
    specialLeaveFtePlbHoursImportCode,
    specialLeaveFteHolidayCompensationImportCode
  ]);

  return (
    <AnimatePresence mode="wait">
      <StyledContainerBox isEditable={isEditable}>
        <MultiStepForm.Section key={'specialLeave'} title={t('title')}>
          <Box>
            <SpecialLeaveFields
              isEditable={isEditable}
              dutyHourTypeOptions={dutyHourTypeOptions}
              handleAddOrUpdateRow={handleAddOrUpdateRow}
              customPlbName={plbName}
              editingRowId={editingRowId}
            />
            <SpecialLeaveTable
              specialLeavesRows={specialLeavesRows}
              columns={columns}
              isEditable={isEditable}
              handleDeleteRows={handleDeleteRows}
              handleEditNominalDuty={handleEditNominalDuty}
            />
          </Box>
        </MultiStepForm.Section>
      </StyledContainerBox>
    </AnimatePresence>
  );
};

export default SpecialLeaveTab;
